import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import useFiltersState from '../reporting_utils/useFiltersState';
import { useEffect } from 'react';
import { getFilterValues, momentDurationToTime } from '../reporting_utils/misc';
import { Common } from '../../utils/common';
import CallPercentages from './CallPercentages';
import ConversionBreakdownGraph from './ConversionBreakdownGraph';
import CallCenterConversionBreakdownGraph from './CallCenterConversionBreakdownGraph';
import TrafficAnalysisTable from './TrafficAnalysisTable';
import useDomainSettings from '../reporting_utils/useDomainSettings';
import Layout from '../reporting_utils/Layout';
import EmployeeBreakdownTable from './EmployeeBreakdownTable';
import LocationBreakdownTable from './LocationBreakdownTable';
import CountWidget from '../reporting_utils/CountWidget';
import moment from 'moment';
import TrafficByHour from './TrafficByHour';

const pageLoadingFrame = window.pageLoadingFrame;

/**
 * @param {number | null} duration
 */
function formatDuration(duration) {
  if (duration === null) {
    duration = 0;
  }
  const momentDuration = moment.duration(duration, 'minutes').clone();
  return momentDurationToTime(momentDuration);
}

const defaultFilterState = { tag_ids: [], employees: [] };

function TrafficAnalysis({ controllerName, domainId }) {
  const [filters, setFilters] = useFiltersState(defaultFilterState, {}, domainId);
  const [data, setData] = useState({
    alert_count: [{ countofalert: 0 }],
    call_percentage: [
      {
        connected_percentage: null,
        missed_percentage: null,
        prospect_percentage: null,
        sales_appt_percentage: null,
        appointmentofferedstr_percentage: null,
      },
    ],
    chat_count: [{ countofchats: 0 }],
    connected_call_count: [{ countofinbound_connectedcalls: 0, countofoutbound_connectedcalls: 0 }],
    conversion_breakdown: [],
    conversion_data: Array(7).fill({ calls: 0, chats: 0, texts: 0, leads: 0 }),
    lead_count: [{ countofleads: 0 }],
    missed_call_count: [{ missed_call_count: 0 }],
    outbound_call_count: [{ outboundcountofcalls: 0 }],
    prospect_breakdown_count: [
      {
        countof_sales_calls: 0,
        countof_service_calls: 0,
        countof_parts_calls: 0,
        countof_one_time_service_calls: 0,
        countof_recurring_service_calls: 0,
        countof_prospect_calls: 0,
        countof_customer_service_calls: 0,
        countof_outbound_not_connected_calls: 0,
        countof_current_patient_calls: 0,
      },
    ],
    sales_appt_count: [{ countofsalesapt: 0 }],
    sales_appt_offered_count: [{ apptoffered: 0 }],
    visitor_count: [{ visitor_count: 0 }],
    employee_traffic: [],
    location_traffic: [],
    traffic_by_hour: [],
    duration: [{ avg_minute_duration: null, total_duration: null }],
  });

  const { isAutomotiveIndustry, industryType, isLoading } = useDomainSettings(
    filters.domains,
    filters.show_inactive
  );

  const fetchData = useCallback(async () => {
    if (!filters.domains) {
      return;
    }

    pageLoadingFrame('show');
    try {
      const res = await fetch('/traffic_analysis', {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(getFilterValues(filters)),
      });

      const {
        alert_count,
        call_percentage,
        chat_count,
        connected_call_count,
        conversion_breakdown,
        conversion_data,
        lead_count,
        missed_call_count,
        outbound_call_count,
        prospect_breakdown_count,
        sales_appt_count,
        sales_appt_offered_count,
        visitor_count,
        employee_traffic,
        location_traffic,
        traffic_by_hour,
        duration,
      } = await res.json();

      setData({
        alert_count,
        call_percentage,
        chat_count,
        connected_call_count,
        conversion_breakdown,
        conversion_data,
        lead_count,
        missed_call_count,
        outbound_call_count,
        prospect_breakdown_count,
        sales_appt_count,
        sales_appt_offered_count,
        visitor_count,
        employee_traffic,
        location_traffic,
        traffic_by_hour,
        duration,
      });
    } catch (error) {
      console.error(error);
      Common.notify('Failed to fetch report data', 'error');
    }
    pageLoadingFrame('hide');
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalCallData = {
    inboundCalls: data.connected_call_count[0].countofinbound_connectedcalls,
    outboundCalls: data.connected_call_count[0].countofoutbound_connectedcalls,
    missedCalls: data.missed_call_count[0].missed_call_count,
  };

  const durationData = {
    totalDuration: data.duration[0].total_duration,
    avgDuration: data.duration[0].avg_minute_duration,
  };

  const employeeData = {
    appointmentsSet: data.sales_appt_count[0].countofsalesapt,
    appointmentsOffered: data.sales_appt_offered_count[0].apptoffered,
    alerts: data.alert_count[0].countofalert,
    outboundCalls: data.outbound_call_count[0].outboundcountofcalls,
  };

  const prospectData = {
    sales: data.prospect_breakdown_count[0].countof_sales_calls,
    service: data.prospect_breakdown_count[0].countof_service_calls,
    parts: data.prospect_breakdown_count[0].countof_parts_calls,
    oneTimeService: data.prospect_breakdown_count[0].countof_one_time_service_calls,
    recurringService: data.prospect_breakdown_count[0].countof_recurring_service_calls,
    prospect: data.prospect_breakdown_count[0].countof_prospect_calls,
    customerService: data.prospect_breakdown_count[0].countof_customer_service_calls,
    outboundNotConnected: data.prospect_breakdown_count[0].countof_outbound_not_connected_calls,
    currentPatient: data.prospect_breakdown_count[0].countof_current_patient_calls,
  };

  const websiteActivity = {
    totalVisitors: data.visitor_count[0].visitor_count,
    totalChats: data.chat_count[0].countofchats,
    totalLeads: data.lead_count[0].countofleads,
  };

  let prospectCallTypes = [
    { label: 'Sales', value: prospectData.sales },
    { label: 'Prospect', value: prospectData.prospect },
  ];
  switch (industryType) {
    case 'Automotive':
      prospectCallTypes = [
        { label: 'Sales', value: prospectData.sales },
        { label: 'Service', value: prospectData.service },
        { label: 'Parts', value: prospectData.parts },
      ];
      break;
    case 'Call Center':
      prospectCallTypes = [
        { label: 'Service', value: prospectData.service },
        { label: 'Parts', value: prospectData.parts },
      ];
      break;
    case 'Pest':
      prospectCallTypes = [
        { label: 'One-Time Service', value: prospectData.oneTimeService },
        { label: 'Recurring Service', value: prospectData.recurringService },
      ];
      break;
    case 'Home Improvement':
      prospectCallTypes = [
        { label: 'Sales', value: prospectData.sales },
        { label: 'Service', value: prospectData.service },
      ];
      break;
    case 'Healthcare':
      prospectCallTypes = [
        { label: 'Prospect', value: prospectData.prospect },
        { label: 'Current Patient', value: prospectData.currentPatient },
      ];
      break;
    case 'Other':
      prospectCallTypes = [
        { label: 'Sales', value: prospectData.sales },
        { label: 'Prospect', value: prospectData.prospect },
      ];
      break;
  }

  let widgetCount = 3;
  if (isAutomotiveIndustry || industryType !== 'Call Center') {
    widgetCount++;
  }

  let colClass = '';
  if (widgetCount === 3) {
    colClass = 'col-lg-4';
  } else if (widgetCount === 4) {
    colClass = 'col-sm-6';
  }

  return (
    <Layout
      title='Traffic Analysis'
      controllerName={controllerName}
      filters={filters}
      fetchData={fetchData}
      setFilters={setFilters}
      defaultFilterState={defaultFilterState}
    >
      <div className='row' style={{ marginBottom: '15px' }}>
        <div className={colClass}>
          <CountWidget
            title='Total Calls'
            href='filtered_call_details/all/all/all/all'
            color='call'
            data={[
              {
                label: 'Inbound Calls',
                value: Common.formatNumberWithCommas(totalCallData.inboundCalls),
              },
              {
                label: 'Outbound Calls',
                value: Common.formatNumberWithCommas(totalCallData.outboundCalls),
              },
              {
                label: 'Missed Calls',
                value: Common.formatNumberWithCommas(totalCallData.missedCalls),
              },
            ]}
          />
        </div>
        <div className={colClass}>
          <CountWidget
            title='Call Duration'
            color='visitors'
            data={[
              {
                label: 'Average Call Duration',
                value: formatDuration(durationData.avgDuration),
              },
              {
                label: 'Total Call Duration',
                value: formatDuration(durationData.totalDuration),
              },
            ]}
          />
        </div>
        <div className={colClass}>
          <CountWidget
            title='Total Prospects'
            color='prospects'
            href='filtered_call_details/all/prospects/all/all'
            data={prospectCallTypes}
          />
        </div>
        {isAutomotiveIndustry ? (
          <div className={colClass}>
            <CountWidget
              title='Employee Performance'
              color='appointments'
              data={[
                {
                  label: 'Appointments Offered',
                  value: Common.formatNumberWithCommas(employeeData.appointmentsOffered),
                },
                {
                  label: 'Appointments Set',
                  value: Common.formatNumberWithCommas(employeeData.appointmentsSet),
                },
                { label: 'Alerts Sent', value: Common.formatNumberWithCommas(employeeData.alerts) },
              ]}
            />
          </div>
        ) : industryType !== 'Call Center' ? (
          <div className={colClass}>
            <CountWidget
              title='Website Activity'
              color='lead'
              data={[
                {
                  label: 'Total Visitors',
                  value: Common.formatNumberWithCommas(websiteActivity.totalVisitors),
                },
                {
                  label: 'Total Chats',
                  value: Common.formatNumberWithCommas(websiteActivity.totalChats),
                },
                {
                  label: 'Total Forms',
                  value: Common.formatNumberWithCommas(websiteActivity.totalLeads),
                },
              ]}
            />
          </div>
        ) : null}
      </div>
      {isAutomotiveIndustry ? <CallPercentages chartData={data.call_percentage} /> : null}
      {industryType !== 'Call Center' ? (
        <ConversionBreakdownGraph
          isAutomotiveIndustry={isAutomotiveIndustry}
          chartData={data.conversion_data}
        />
      ) : (
        <CallCenterConversionBreakdownGraph
          isAutomotiveIndustry={isAutomotiveIndustry}
          chartData={data.conversion_data}
        />
      )}
      <TrafficByHour chartData={data.traffic_by_hour} />
      {isLoading ? null : (
        <>
          <TrafficAnalysisTable
            isAutomotiveIndustry={isAutomotiveIndustry}
            tableData={data.conversion_breakdown}
            industryType={industryType}
          />
          <EmployeeBreakdownTable data={data.employee_traffic} />
          <LocationBreakdownTable data={data.location_traffic} industryType={industryType} />
        </>
      )}
    </Layout>
  );
}

TrafficAnalysis.propTypes = {
  controllerName: PropTypes.string.isRequired,
  domainId: PropTypes.string,
};

export default TrafficAnalysis;
